import { useState } from 'react';
import PropTypes from 'prop-types';
import { Flex, Box } from '@chakra-ui/react';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import Text from 'ui/Text';

function Pagination({
  activePage,
  pageSize,
  totalItemCount,
  pagesRange = 7,
  onChange,
}) {
  const pageButtons = [];
  const totalPages = Math.ceil(totalItemCount / pageSize);
  let firstPage;
  let lastPage;

  if (totalPages <= pagesRange) {
    firstPage = 1;
    lastPage = totalPages;
  } else if (activePage < Math.ceil(pagesRange / 2)) {
    firstPage = 1;
    lastPage = Math.min(pagesRange - 1, totalPages);
  } else if (activePage >= totalPages - Math.floor(pagesRange / 2)) {
    lastPage = totalPages;
    firstPage = Math.max(totalPages - pagesRange + 2, 1);
  } else {
    firstPage = activePage - Math.floor(pagesRange / 2) + 1;
    lastPage = activePage + Math.floor(pagesRange / 2) - 1;
  }

  for (let i = firstPage; i <= lastPage; i += 1) {
    pageButtons.push(
      <Box
        key={`page-${i}`}
        id={`page-${i}`}
        aria-label={`Page ${i}${i === activePage ? ' active' : ''}`}
        onClick={() => onChange(i)}
        border="none"
        p="12px 8px"
        borderRadius="8px 0 0 8px"
        cursor="pointer"
        color={i === activePage ? 'primary.default' : 'semantics.default'}
        _hover={{ textDecoration: 'underline' }}
      >
        <Text fontWeight="bold">{i}</Text>
      </Box>
    );
  }

  const [hoverPrev, setHoverPrev] = useState(false);
  const [hoverNext, setHoverNext] = useState(false);

  return (
    <Flex width="min-content" borderRadius="8px" bgColor="gray.50">
      <Box
        border="none"
        p="12px 8px"
        borderRadius="8px 0 0 8px"
        mr="8px"
        disabled={activePage <= 1}
        aria-label="Previous Page"
        cursor="pointer"
        onClick={() => activePage > 1 && onChange(activePage - 1)}
        // eslint-disable-next-line no-nested-ternary
        color={
          activePage <= 1
            ? 'gray.600'
            : hoverPrev
            ? 'white'
            : 'semantics.default'
        }
        _hover={{
          backgroundColor: activePage <= 1 ? 'none' : 'semantics.default',
        }}
        _disabled={{ cursor: 'default' }}
        onMouseEnter={() => setHoverPrev(true)}
        onMouseLeave={() => setHoverPrev(false)}
      >
        <FaChevronLeft />
      </Box>
      {firstPage > 1 && (
        <>
          <Box
            id="page-1"
            aria-label="Page 1"
            onClick={() => onChange(1)}
            border="none"
            p="12px 8px"
            borderRadius="8px 0 0 8px"
            cursor="pointer"
            color={activePage === 1 ? 'primary.default' : 'semantics.default'}
            _hover={{ textDecoration: 'underline' }}
          >
            <Text fontWeight="bold">{1}</Text>
          </Box>
          {firstPage > 2 && <span style={{ padding: '12px 0' }}>&hellip;</span>}
        </>
      )}
      {pageButtons}
      {lastPage < totalPages - 1 && (
        <>
          <span style={{ padding: '12px 0' }}>&hellip;</span>
          <Box
            id={`page-${totalPages}`}
            aria-label={`Page ${totalPages}`}
            onClick={() => onChange(totalPages)}
            border="none"
            p="12px 8px"
            borderRadius="8px 0 0 8px"
            cursor="pointer"
            color={
              activePage === totalPages
                ? 'primary.default'
                : 'semantics.default'
            }
            _hover={{ textDecoration: 'underline' }}
          >
            <Text fontWeight="bold">{totalPages}</Text>
          </Box>
        </>
      )}
      <Box
        border="none"
        p="12px 8px"
        borderRadius="0 8px 8px 0"
        ml="8px"
        disabled={activePage >= totalPages}
        aria-label="Next Page"
        cursor="pointer"
        onClick={() => activePage < totalPages && onChange(activePage + 1)}
        color={
          // eslint-disable-next-line no-nested-ternary
          activePage >= totalPages
            ? 'gray.600'
            : hoverNext
            ? 'white'
            : 'semantics.default'
        }
        _hover={{
          backgroundColor:
            activePage >= totalPages ? 'none' : 'semantics.default',
        }}
        _disabled={{ cursor: 'default' }}
        onMouseEnter={() => setHoverNext(true)}
        onMouseLeave={() => setHoverNext(false)}
      >
        <FaChevronRight />
      </Box>
    </Flex>
  );
}

Pagination.propTypes = {
  activePage: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  totalItemCount: PropTypes.number.isRequired,
  pagesRange: PropTypes.number,
  onChange: PropTypes.func,
};

export default Pagination;
